"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import React, { type ReactNode, useCallback, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import {
  TurnstileVerification,
  type TurnstileVerificationHandle,
} from "@/components/auth/TurnstileVerification";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { addMessage } from "@/components/Messages";
import { GoogleLogo } from "@/components/svgs/Google";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { env } from "@/env";
import { useGoogleOAuth } from "@/lib/auth/useGoogleOAuth";
import { getUserSignUpMetadata } from "@/lib/auth/utils";
import {
  buildHomepageExperimentSignUpMetadata,
  type HomepageEntryVariant,
} from "@/lib/homepage-entry-experiment";
import Link from "@/lib/locale/Link";
import { useSignUpEmailPassword } from "@/lib/nhost/react";

export type AuthEmailPasswordFormData = {
  email: string;
  password: string;
};

export const getSignInHrefForDestination = (destination: string) =>
  `/app/auth/sign-in?toPath=${encodeURIComponent(destination)}`;

type SignUpFormFieldsProps = {
  defaultValues?: AuthEmailPasswordFormData;
  isGoogleLoading?: boolean;
  isLoading?: boolean;
  onGoogleSignUp: () => void;
  onSubmit: SubmitHandler<AuthEmailPasswordFormData>;
  signInHref?: string;
  turnstileToken: string | null;
  turnstileVerification: ReactNode;
};

export const SignUpFormFields = ({
  defaultValues = { email: "", password: "" },
  isGoogleLoading = false,
  isLoading = false,
  onGoogleSignUp,
  onSubmit,
  signInHref = "/app/auth/sign-in",
  turnstileToken,
  turnstileVerification,
}: SignUpFormFieldsProps) => {
  const t = useTranslations();
  const formSchema = z.object({
    email: z.string().email({
      message: t("auth.emailInputError"),
    }),
    password: z.string().min(9, {
      message: t("auth.passwordInputError"),
    }),
  });
  const form = useForm<AuthEmailPasswordFormData>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  return (
    <div className="mx-4 w-full max-w-sm rounded-lg border bg-card p-4 md:p-8">
      <h1 className="text-center text-2xl font-semibold">{t("auth.signUpHeader")}</h1>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg">{t("auth.emailInputLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      className="text-lg"
                      spellCheck={false}
                      type="email"
                      placeholder={t("auth.emailInputPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg">{t("auth.passwordInputLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="new-password"
                      className="text-lg"
                      type="password"
                      placeholder={t("auth.passwordInputPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {turnstileVerification}
            <Button
              aria-busy={isLoading}
              loading={isLoading}
              disabled={isLoading || !turnstileToken}
              type="submit"
              className="w-full text-lg"
            >
              {t("auth.signUpSubmitButton")}
            </Button>
          </form>
        </Form>
        <Button
          aria-busy={isGoogleLoading}
          disabled={isGoogleLoading}
          onClick={onGoogleSignUp}
          className="w-full mt-4 space-x-2 text-lg"
          variant="outline"
          type="button"
        >
          <span>{t("auth.signUpWithGoogleButton")}</span>
          <GoogleLogo className="w-8" />
        </Button>
      </div>
      <div className="mt-8 space-y-4 text-foreground/80 text-sm text-center">
        {t.rich("auth.alreadyRegisteredLogin", {
          prefix: (chunks) => <span>{chunks}</span>,
          loginLink: (chunks) => (
            <Link href={signInHref} className="underline">
              {chunks}
            </Link>
          ),
        })}
        <div>
          <Link href="/app/auth/forgot-password" className="underline">
            {t("auth.forgottenPasswordLink")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const SignUpForm = ({
  toPath,
  homepageEntryVariant,
}: {
  toPath: string;
  homepageEntryVariant?: HomepageEntryVariant;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const posthog = usePostHog();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const emailSubmissionInFlightRef = useRef(false);
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileRef = useRef<TurnstileVerificationHandle>(null);

  const handleTurnstileTokenChange = (token: string | null) => {
    turnstileTokenRef.current = token;
    setTurnstileToken(token);
  };

  const handleSubmitForm: SubmitHandler<AuthEmailPasswordFormData> = async (formData) => {
    const submittedToken = turnstileTokenRef.current;
    if (emailSubmissionInFlightRef.current || !submittedToken) return;
    emailSubmissionInFlightRef.current = true;
    turnstileTokenRef.current = null;
    setTurnstileToken(null);
    let returnsControlToForm = true;

    try {
      posthog?.capture("signup_form_submitted", { method: "emailPassword" });
      const destination = new URL(toPath, window.location.origin);
      const signUpMetadata = getUserSignUpMetadata();
      const homepageExperimentMetadata = buildHomepageExperimentSignUpMetadata({
        distinctId: posthog?.get_distinct_id(),
        variant: homepageEntryVariant,
      });
      const isFromSongs = signUpMetadata.signupEntryCampaign === "add-subs-to-songs";
      if (isFromSongs) {
        destination.searchParams.set("fromSongs", "1");
      }

      const response = await signUpEmailPassword(
        formData.email,
        formData.password,
        {
          redirectTo: destination.href,
          locale: locale || "en",
          metadata: {
            ...signUpMetadata,
            ...homepageExperimentMetadata,
            signUpMethod: "emailPassword",
          },
        },
        {
          headers: {
            "x-cf-turnstile-response": submittedToken,
          },
        }
      ).catch((error: unknown) => {
        turnstileRef.current?.resetAfterSubmission();
        posthog?.capture("signup_failed", {
          method: "emailPassword",
          reason: "network_error",
        });
        console.warn(error);
        addMessage({ text: t("auth.signUpErrorMessage"), type: "ERROR" });
        return null;
      });

      if (!response) return;

      if (response.isSuccess) {
        posthog?.capture("signup_succeeded", { method: "emailPassword" });
        // Redirect to dashboard
        window.location.href = destination.pathname + destination.search + destination.hash;
        returnsControlToForm = false;
        return <LoadingScreen />;
      }
      turnstileRef.current?.resetAfterSubmission();
      if (response.needsEmailVerification) {
        posthog?.capture("signup_succeeded", {
          method: "emailPassword",
          needs_email_verification: true,
        });
        addMessage({
          text: t("auth.needEmailValidationMessage"),
          type: "SUCCESS",
        });
      } else {
        posthog?.capture("signup_failed", {
          method: "emailPassword",
          reason: response.error?.error || response.error?.message || "unknown",
          status: response.error?.status,
        });
        console.warn(response);
        addMessage({
          text: response.error?.message || t("auth.signUpErrorMessage"),
          type: "ERROR",
        });
      }
    } finally {
      if (returnsControlToForm) emailSubmissionInFlightRef.current = false;
    }
  };

  const prepareGoogleAttempt = useCallback(() => {
    const destination = new URL(toPath, window.location.origin);
    const signUpMetadata = getUserSignUpMetadata();
    const homepageExperimentMetadata = buildHomepageExperimentSignUpMetadata({
      distinctId: posthog?.get_distinct_id(),
      variant: homepageEntryVariant,
    });
    const isFromSongs = signUpMetadata.signupEntryCampaign === "add-subs-to-songs";
    if (isFromSongs) {
      destination.searchParams.set("fromSongs", "1");
    }

    return {
      destination: destination.pathname + destination.search + destination.hash,
      metadata: {
        ...signUpMetadata,
        ...homepageExperimentMetadata,
        signUpMethod: "google",
      },
    };
  }, [homepageEntryVariant, posthog, toPath]);
  const handleGoogleFailure = useCallback(() => {
    addMessage({ text: t("auth.googleOAuthStartError"), type: "ERROR" });
  }, [t]);
  const handleGoogleStart = useCallback(() => {
    posthog?.capture("signup_form_submitted", { method: "google" });
  }, [posthog]);
  const { isStarting, startGoogleOAuth } = useGoogleOAuth({
    entryPoint: "sign-up",
    locale: locale || "en",
    onFailure: handleGoogleFailure,
    onStart: handleGoogleStart,
    prepareAttempt: prepareGoogleAttempt,
  });

  const { signUpEmailPassword, isLoading } = useSignUpEmailPassword();
  const signInHref = getSignInHrefForDestination(toPath);

  return (
    <SignUpFormFields
      isLoading={isLoading}
      isGoogleLoading={isStarting}
      onGoogleSignUp={startGoogleOAuth}
      onSubmit={handleSubmitForm}
      signInHref={signInHref}
      turnstileToken={turnstileToken}
      turnstileVerification={
        <TurnstileVerification
          ref={turnstileRef}
          siteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
          surface="sign_up"
          onTokenChange={handleTurnstileTokenChange}
        />
      }
    />
  );
};
